import { ArrowsClockwise, ShieldCheck, Sparkle, SquaresFour } from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import type { Expertise } from '../../content/site';

export const EXPERTISE_ICONS: Record<Expertise['icon'], Icon> = {
	Sparkle,
	ArrowsClockwise,
	SquaresFour,
	ShieldCheck,
};

export type Surface = {
	/** Surface fill (and border when the fill matches the page). */
	bg: string;
	/** True when `bg` draws a 1px border (kept a hairline while Flip scales the surface). */
	hairline?: boolean;
	/** Primary ink. */
	ink: string;
	/** Secondary ink, AA on the surface. */
	soft: string;
	/** Icon and list marks. */
	mark: string;
	/** Stack tags. */
	tag: string;
	/** Hairline for small outlined controls on the surface. */
	edge: string;
	/** Inner ring drawn with the global Solar outline, so keyboard focus reads on every surface. */
	focus: string;
};

/**
 * One surface per slice, for rhythm (bento background diversity). Contrast:
 * Graphite on Sage 8.2:1 (never Graphite Muted on Sage), Graphite Muted on Mist 5.2:1
 * and on Mineral 5.9:1, Mineral on Forest 8.8:1 (Mineral 80% 6.3:1), Solar marks on
 * Forest 5.9:1, Forest marks on the light surfaces 5.4:1 or more.
 * Dark theme: Mineral on Forest Deep / Forest / Forest Night (13:1, 8.8:1, 16:1), Dusk Muted
 * on Forest Deep (7.5:1) and Forest Night (8.9:1), Solar marks and focus rings on all of them.
 */
export const SURFACES: Surface[] = [
	{
		bg: 'bg-sage dark:bg-forest-deep',
		ink: 'text-graphite dark:text-mineral',
		soft: 'text-graphite dark:text-dusk-muted',
		mark: 'text-forest dark:text-solar',
		tag: 'bg-mineral/60 text-graphite dark:bg-mineral/10 dark:text-mineral',
		edge: 'border-graphite/15 dark:border-mineral/20',
		focus: 'focus-visible:shadow-[inset_0_0_0_2px_#252B27] dark:focus-visible:shadow-[inset_0_0_0_2px_#E8C96A]',
	},
	{
		bg: 'bg-mist dark:bg-forest',
		ink: 'text-graphite dark:text-mineral',
		soft: 'text-graphite-muted dark:text-mineral/80',
		mark: 'text-forest dark:text-solar',
		tag: 'bg-mineral text-graphite dark:bg-mineral/10 dark:text-mineral',
		edge: 'border-graphite/15 dark:border-mineral/25',
		focus: 'focus-visible:shadow-[inset_0_0_0_2px_#252B27] dark:focus-visible:shadow-[inset_0_0_0_2px_#E8C96A]',
	},
	{
		bg: 'bg-forest dark:bg-forest-hover',
		ink: 'text-mineral',
		soft: 'text-mineral/80',
		mark: 'text-solar',
		tag: 'border border-mineral/25 text-mineral',
		edge: 'border-mineral/25',
		focus: 'focus-visible:shadow-[inset_0_0_0_2px_#F7F7F0]',
	},
	{
		bg: 'border border-graphite/10 bg-mineral dark:border-mineral/10 dark:bg-forest-night',
		hairline: true,
		ink: 'text-graphite dark:text-mineral',
		soft: 'text-graphite-muted dark:text-dusk-muted',
		mark: 'text-forest dark:text-solar',
		tag: 'bg-mist text-graphite dark:bg-mineral/10 dark:text-mineral',
		edge: 'border-graphite/15 dark:border-mineral/20',
		focus: 'focus-visible:shadow-[inset_0_0_0_2px_#252B27] dark:focus-visible:shadow-[inset_0_0_0_2px_#E8C96A]',
	},
];

export const surfaceAt = (index: number): Surface => SURFACES[index % SURFACES.length];
